/**
 * Attendance (fingerprint) — hub for HR and branch managers (spec ATTENDANCE_FEATURE_SPEC.md §4.1).
 *
 * Tabs, shown according to GET /api/attendance/my-access:
 *   Overview      — every branch at a glance, device health                       (HR)
 *   Suggestions   — the inbox: one click per fix, reason required                 (HR, managers: own team)
 *   Employee      — month calendar, day detail, fixes and undo                    (HR, managers: own team)
 *   Timesheets    — approve / finalise / release / reopen                         (HR; managers read)
 *   Sheet         — one employee's pay period in the Excel layout, print, export  (HR, managers: own team)
 *   Pay preview   — estimated pay                                                 (salary-permission roles)
 *   Off days      — standing off days, changes for one month, monthly review       (HR, managers: own team)
 *   Settings      — devices, ID matching, holidays, rules                         (HR managers)
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Fingerprint, Inbox, LayoutDashboard, Loader2, CalendarDays, CalendarOff, ClipboardCheck, Wallet, Settings, Sheet } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { api, errorText } from './shared';
import OverviewTab from './OverviewTab';
import InboxTab from './InboxTab';
import EmployeeMonthTab from './EmployeeMonthTab';
import TimesheetsTab from './TimesheetsTab';
import SheetTab from './SheetTab';
import PayTab from './PayTab';
import SettingsTab from './SettingsTab';
import OffDaysTab from './OffDaysTab';

export default function AttendancePage() {
  const { language } = useLanguage();
  const { token } = useAuth();
  const isRTL = language === 'ar';
  const t = (en, ar) => (isRTL ? ar : en);
  const [params, setParams] = useSearchParams();
  const [access, setAccess] = useState(null);
  const [error, setError] = useState('');
  const [reminder, setReminder] = useState(null);

  useEffect(() => {
    api(token).get('/my-access').then(setAccess).catch((e) => setError(errorText(e)));
    api(token).get('/off-days/reminder').then(setReminder).catch(() => setReminder(null));
  }, [token]);

  const tabs = access ? [
    access.hr_scope && { key: 'overview', icon: LayoutDashboard, label: t('Overview', 'نظرة عامة') },
    access.can_review && { key: 'suggestions', icon: Inbox, label: t('Suggestions', 'المقترحات') },
    access.can_review && { key: 'employee', icon: CalendarDays, label: t('Employee month', 'شهر الموظف') },
    access.can_review && { key: 'timesheets', icon: ClipboardCheck, label: t('Timesheets', 'كشوف الدوام') },
    access.can_review && { key: 'sheet', icon: Sheet, label: t('Timesheet sheet', 'كشف الدوام التفصيلي') },
    access.can_review && { key: 'offdays', icon: CalendarOff, label: t('Off days', 'أيام الراحة') },
    access.can_see_pay && { key: 'pay', icon: Wallet, label: t('Pay preview', 'معاينة الرواتب') },
    access.can_manage && { key: 'settings', icon: Settings, label: t('Settings', 'الإعدادات') },
  ].filter(Boolean) : [];

  const tab = tabs.some((x) => x.key === params.get('tab')) ? params.get('tab') : tabs[0]?.key;
  const go = useCallback((next, extra = {}) => {
    const p = new URLSearchParams(params);
    p.set('tab', next);
    Object.entries(extra).forEach(([k, v]) => (v ? p.set(k, v) : p.delete(k)));
    setParams(p);
  }, [params, setParams]);

  return (
    <div className="p-6 space-y-6" dir={isRTL ? 'rtl' : 'ltr'} data-testid="attendance-page">
      <div className="flex items-center gap-3">
        <Fingerprint className="h-7 w-7 text-brand-orange" />
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t('Attendance', 'الحضور والانصراف')}</h1>
          <p className="text-sm text-slate-500">
            {t('Fingerprint punches, worked out automatically. Every fix is one click, needs a reason and can be undone.',
              'بصمات الحضور تُحسب تلقائياً. كل تصحيح بنقرة واحدة، مع سبب، ويمكن التراجع عنه.')}
          </p>
        </div>
      </div>

      {error && <div className="text-sm text-rose-600" data-testid="attendance-error">{error}</div>}
      {reminder?.due && (
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
          data-testid="offdays-reminder">
          <CalendarOff className="h-4 w-4" />
          <span>
            {t(`Please review your team's off days for the pay period ${reminder.period.start} – ${reminder.period.end} by ${reminder.closes}. If you change nothing, the current off days carry over.`,
              `يرجى مراجعة أيام الراحة لفريقك لفترة الراتب ${reminder.period.start} – ${reminder.period.end} قبل ${reminder.closes}. إن لم تغيّر شيئاً تستمر أيام الراحة الحالية.`)}
          </span>
          <button type="button" className="ms-auto underline font-medium" onClick={() => go('offdays', { period: reminder.period.key })}>
            {t('Review off days', 'مراجعة أيام الراحة')}
          </button>
        </div>
      )}
      {!access && !error && <Loader2 className="h-6 w-6 animate-spin text-slate-400" />}
      {access && !tabs.length && (
        <div className="brand-empty-state p-8 text-center text-slate-500" data-testid="attendance-no-access">
          {t('Attendance review is for HR and branch managers. Your own attendance is under My attendance.',
            'مراجعة الحضور لقسم الموارد البشرية ومديري الفروع. حضورك الشخصي في «حضوري».')}
        </div>
      )}

      {tab && (
        <Tabs value={tab} onValueChange={(v) => go(v)}>
          <TabsList data-testid="attendance-tabs">
            {tabs.map(({ key, icon: Icon, label }) => (
              <TabsTrigger key={key} value={key} data-testid={`attendance-tab-${key}`}>
                <Icon className="h-4 w-4 me-1.5" />{label}
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="overview"><OverviewTab token={token} isRTL={isRTL} go={go} /></TabsContent>
          <TabsContent value="suggestions"><InboxTab token={token} isRTL={isRTL} go={go} /></TabsContent>
          <TabsContent value="employee">
            <EmployeeMonthTab token={token} isRTL={isRTL} access={access}
              employeeId={params.get('employee')} month={params.get('month')}
              onSelect={(employee, month) => go('employee', { employee, month })} />
          </TabsContent>
          <TabsContent value="timesheets"><TimesheetsTab token={token} isRTL={isRTL} access={access} go={go} /></TabsContent>
          <TabsContent value="sheet">
            <SheetTab token={token} isRTL={isRTL} employeeId={params.get('employee')} month={params.get('month')}
              onSelect={(employee, month) => go('sheet', { employee, month })} />
          </TabsContent>
          <TabsContent value="offdays">
            <OffDaysTab token={token} isRTL={isRTL} access={access} period={params.get('period')} managerId={params.get('manager')}
              onSelect={(period, manager) => go('offdays', { period, manager })}
              onConfirmed={() => setReminder((r) => (r ? { ...r, due: false } : r))} />
          </TabsContent>
          <TabsContent value="pay"><PayTab token={token} isRTL={isRTL} /></TabsContent>
          <TabsContent value="settings"><SettingsTab token={token} isRTL={isRTL} /></TabsContent>
        </Tabs>
      )}
    </div>
  );
}
